"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkMountList = exports.findMountConflict = exports.validateMountMode = exports.validateMountContainerPath = exports.resolveHomePath = exports.validateDataRootPath = exports.isHomeRelativePath = exports.validateMountHostPath = exports.isSameOrBelow = exports.isCleanAbsolutePath = exports.RESERVED_CONTAINER_PATHS = exports.MOUNT_HOST_PATH_PATTERN = exports.MAX_MOUNT_LIST_ENTRIES = exports.MAX_CONTAINER_PATH_LENGTH = exports.MAX_HOST_PATH_LENGTH = exports.MOUNT_REFUSAL_REASONS = exports.MOUNT_MODES = void 0;
exports.MOUNT_MODES = ["ro", "rw"];
exports.MOUNT_REFUSAL_REASONS = [
    "host_path_not_absolute",
    "host_path_forbidden_character",
    "host_path_is_root",
    "host_path_not_normalized",
    "container_path_invalid",
    "container_path_reserved",
    "invalid_mode",
    "duplicate_container_path",
    "nested_container_path",
    "overlapping_host_path",
    "no_data_roots",
    "outside_data_roots",
    "mode_exceeds_root",
    "path_too_long",
    "too_many_entries",
];
exports.MAX_HOST_PATH_LENGTH = 1024;
exports.MAX_CONTAINER_PATH_LENGTH = 512;
exports.MAX_MOUNT_LIST_ENTRIES = 64;
exports.MOUNT_HOST_PATH_PATTERN = /^\/[A-Za-z0-9._/@%+-]*$/;
exports.RESERVED_CONTAINER_PATHS = [
    "/workspace",
    "/tmp",
    "/var/tmp",
];
const CONTAINER_PATH_PATTERN = /^\/mnt\/([A-Za-z0-9._-]+)$/;
const ok = { kind: "ok" };
const refuse = (reason, detail) => ({
    kind: "refused",
    reason,
    detail,
});
const isCleanAbsolutePath = (value) => {
    if (!value.startsWith("/") || /[:\0\r\n]/.test(value))
        return false;
    if (value === "/")
        return true;
    return value
        .split("/")
        .slice(1)
        .every((segment) => segment !== "" && segment !== "." && segment !== "..");
};
exports.isCleanAbsolutePath = isCleanAbsolutePath;
const isSameOrBelow = (child, parent) => child === parent ||
    child.startsWith(parent.endsWith("/") ? parent : `${parent}/`);
exports.isSameOrBelow = isSameOrBelow;
const nests = (a, b) => (0, exports.isSameOrBelow)(a, b) || (0, exports.isSameOrBelow)(b, a);
const validateMountHostPath = (hostPath) => {
    if (!hostPath.startsWith("/")) {
        return refuse("host_path_not_absolute", `The host path ${JSON.stringify(hostPath)} is not absolute.`);
    }
    if (hostPath.length > exports.MAX_HOST_PATH_LENGTH) {
        return refuse("path_too_long", `The host path is ${hostPath.length} characters long; the limit is ${exports.MAX_HOST_PATH_LENGTH}.`);
    }
    if (!exports.MOUNT_HOST_PATH_PATTERN.test(hostPath)) {
        return refuse("host_path_forbidden_character", `The host path ${JSON.stringify(hostPath)} may use only letters, digits and . _ / @ % + -.`);
    }
    if (hostPath === "/") {
        return refuse("host_path_is_root", "The host path cannot be the root directory.");
    }
    if (!(0, exports.isCleanAbsolutePath)(hostPath)) {
        return refuse("host_path_not_normalized", `The host path ${JSON.stringify(hostPath)} must be normalized: no "." or ".." segments, no doubled or trailing slashes.`);
    }
    return ok;
};
exports.validateMountHostPath = validateMountHostPath;
const isHomeRelativePath = (value) => value === "~" || value.startsWith("~/");
exports.isHomeRelativePath = isHomeRelativePath;
const validateDataRootPath = (path) => {
    if (!(0, exports.isHomeRelativePath)(path)) {
        if (!path.startsWith("/")) {
            return refuse("host_path_not_absolute", `The folder ${JSON.stringify(path)} is not absolute: write an absolute path, or ~ or ~/<path> for a folder in each user's home.`);
        }
        return (0, exports.validateMountHostPath)(path);
    }
    if (path.length > exports.MAX_HOST_PATH_LENGTH) {
        return refuse("path_too_long", `The folder is ${path.length} characters long; the limit is ${exports.MAX_HOST_PATH_LENGTH}.`);
    }
    if (path === "~")
        return ok;
    const below = path.slice(1);
    if (!exports.MOUNT_HOST_PATH_PATTERN.test(below)) {
        return refuse("host_path_forbidden_character", `The folder ${JSON.stringify(path)} may use only letters, digits and . _ / @ % + - after the ~.`);
    }
    if (below === "/" || !(0, exports.isCleanAbsolutePath)(below)) {
        return refuse("host_path_not_normalized", `The folder ${JSON.stringify(path)} must be normalized: no "." or ".." segments, no doubled or trailing slashes.`);
    }
    return ok;
};
exports.validateDataRootPath = validateDataRootPath;
const resolveHomePath = (path, home) => {
    if (!(0, exports.isHomeRelativePath)(path))
        return path;
    if ((0, exports.validateDataRootPath)(path).kind !== "ok")
        return null;
    if (home === null || !(0, exports.isCleanAbsolutePath)(home))
        return null;
    const below = path.slice(1);
    const resolved = home === "/" ? below || "/" : `${home}${below}`;
    return (0, exports.validateMountHostPath)(resolved).kind === "ok" ? resolved : null;
};
exports.resolveHomePath = resolveHomePath;
const validateMountContainerPath = (containerPath) => {
    if (containerPath.length > exports.MAX_CONTAINER_PATH_LENGTH) {
        return refuse("path_too_long", `The container path is ${containerPath.length} characters long; the limit is ${exports.MAX_CONTAINER_PATH_LENGTH}.`);
    }
    const match = CONTAINER_PATH_PATTERN.exec(containerPath);
    if (match === null || match[1] === "." || match[1] === "..") {
        return refuse("container_path_invalid", `The container path ${JSON.stringify(containerPath)} must be /mnt/<name>, where <name> uses only letters, digits, ".", "_" and "-".`);
    }
    return ok;
};
exports.validateMountContainerPath = validateMountContainerPath;
const validateMountMode = (mode) => exports.MOUNT_MODES.includes(mode)
    ? ok
    : refuse("invalid_mode", `The mount mode ${JSON.stringify(mode)} is not "ro" or "rw".`);
exports.validateMountMode = validateMountMode;
const findMountConflict = (mounts) => {
    for (let i = 0; i < mounts.length; i++) {
        const reserved = exports.RESERVED_CONTAINER_PATHS.find((path) => nests(mounts[i].containerPath, path));
        if (reserved !== undefined) {
            return {
                kind: "refused",
                index: i,
                reason: "container_path_reserved",
                detail: `The container path ${mounts[i].containerPath} overlaps the reserved ${reserved}.`,
            };
        }
        for (let j = 0; j < i; j++) {
            if (mounts[i].containerPath === mounts[j].containerPath) {
                return {
                    kind: "refused",
                    index: i,
                    reason: "duplicate_container_path",
                    detail: `Mounts ${j} and ${i} both use the container path ${mounts[i].containerPath}.`,
                };
            }
            if (nests(mounts[i].containerPath, mounts[j].containerPath)) {
                return {
                    kind: "refused",
                    index: i,
                    reason: "nested_container_path",
                    detail: `The container paths ${mounts[j].containerPath} and ${mounts[i].containerPath} nest.`,
                };
            }
            if (nests(mounts[i].hostPath, mounts[j].hostPath)) {
                return {
                    kind: "refused",
                    index: i,
                    reason: "overlapping_host_path",
                    detail: `The host paths ${mounts[j].hostPath} and ${mounts[i].hostPath} overlap.`,
                };
            }
        }
    }
    return { kind: "ok" };
};
exports.findMountConflict = findMountConflict;
const checkMountList = (mounts) => {
    if (mounts.length > exports.MAX_MOUNT_LIST_ENTRIES) {
        return {
            kind: "refused",
            index: exports.MAX_MOUNT_LIST_ENTRIES,
            reason: "too_many_entries",
            detail: `A workspace may list at most ${exports.MAX_MOUNT_LIST_ENTRIES} mounts; this list has ${mounts.length}.`,
        };
    }
    for (let index = 0; index < mounts.length; index++) {
        const mount = mounts[index];
        for (const check of [
            (0, exports.validateMountHostPath)(mount.hostPath),
            (0, exports.validateMountContainerPath)(mount.containerPath),
            (0, exports.validateMountMode)(mount.mode),
        ]) {
            if (check.kind === "refused") {
                return {
                    kind: "refused",
                    index,
                    reason: check.reason,
                    detail: check.detail,
                };
            }
        }
    }
    return (0, exports.findMountConflict)(mounts);
};
exports.checkMountList = checkMountList;
